// migrated 1:1 from @nexato/nx-core-module, #688
import StarterKit from "@tiptap/starter-kit";
import { Activity } from "../../../entities/activity/activity";
import { ActivityComponent } from "./activityComponent";
import { Component, computed, effect, inject, input, model, output, signal, TemplateRef, viewChild } from '@angular/core';
import { generateHTML } from '@tiptap/core';
import { CommandInput, CommandTemplate } from "../../../entities/commandTemplate/commandTemplate";
import { CommandService, Reloadable } from "../../../services/command/command.service";
import { RefetchCoordinator } from "../../../services/refetch-coordinator/refetch-coordinator.service";

@Component({
  selector: 'app-note-created',
  template: `
    <div class="border border-gray bg-white p-2 pt-1 pb-1 rounded mb-2">
      <div class="flex flex-row items-center mb-2 min-h-[28px]">
        <div class="flex-1">
          <b>{{activity()?.actor?.displayName}}</b>&nbsp;<app-relative-time [timestamp]="activity()?.createdDateTime" /><ng-container *ngIf="activity()?.viewModel?.updated">&nbsp;<span class="text-gray-500">(bearbeitet)</span></ng-container>
        </div>
        <div class="flex-shrink-0">
          <nx-default-button
            *ngIf="editCommandTemplate() && !isEditMode"
            [style]="'light'"
            [tooltip]="'Kommentar bearbeiten'"
            (clicked)="editComment()"
            [disabled]="editCommandTemplate()?.settings?.disabled"
            [icon]="editCommandTemplate()?.settings?.icon">
          </nx-default-button>
          <nx-default-button
            *ngIf="deleteCommandTemplate() && !isEditMode"
            class="ml-1"
            [disabled]="deleteCommandTemplate()?.settings?.disabled"
            [style]="'light'"
            (clicked)="deleteComment()"
            [tooltip]="'Kommentar löschen'"
            [icon]="deleteCommandTemplate()?.settings?.icon">
          </nx-default-button>
        </div>
      </div>

      <ng-container *ngIf="!isEditMode; else editCommentTemplate">
        <ng-container *ngTemplateOutlet="showCommentTemplate"></ng-container>
      </ng-container>
    </div>

    <!-- show comment template -->
    <ng-template #showCommentTemplate>
      <div class="mb-1 nx-view-tipTap" [innerHTML]="commentHtml()"></div>
      <app-activity-owner-badge
        class="mt-2.5 mb-1"
        [owner]="activity()?.owner"
        [ownerId]="ownerId()">
      </app-activity-owner-badge>
    </ng-template>

    <!-- edit comment template -->
    <ng-template #editCommentTemplate>
      <nx-edit-tipTap-input
        (isEmptyChange)="onEmptyStateChange($event)"
        [ngModel]="editText()"
        (ngModelChange)="editText.set($event)">
      </nx-edit-tipTap-input>
      <div class="mt-2">
        <nx-default-button
            [style]="'dark'"
            [disabled]="isEditorEmpty || !hasContentChanged()"
            (click)="updateComment()"
            [label]="'Kommentar speichern'">
        </nx-default-button>
        <nx-default-button
          class="ml-2"
            [style]="'dark'"
            (click)="cancelUpdateComment()"
            [label]="'Abbrechen'">
        </nx-default-button>
      </div>
    </ng-template>
  `
})
export class NoteCreatedComponent implements ActivityComponent, Reloadable {

  // Computed from activity
  readonly editCommandTemplate = computed(() =>
    this.activity()?.viewModel?.commands?.find(c => c.name === 'noteUpdateCommand')
  );
  readonly deleteCommandTemplate = computed(() =>
    this.activity()?.viewModel?.commands?.find(c => c.name === 'noteDeleteCommand')
  );

  // For editing: local copy that can be modified
  editText = signal<any>(undefined);

  // For display: computed from activity
  readonly commentHtml = computed(() => {
    const comment = this.activity()?.viewModel?.content;
    if (!comment) return '';
    try {
      return generateHTML(comment, [StarterKit]);
    } catch (error) {
      console.error(`Failed to render content for note ${this.activity()?.viewModel?.noteId}`, error);
      return '';
    }
  });

  isEditorEmpty: boolean = true;

  readonly reloaded = output<void>();

  isEditMode: boolean = false;

  readonly activity = input.required<Activity>();
  readonly ownerId = input<string>();

  private readonly indicatorTemplate = viewChild<TemplateRef<unknown>>('customIndicator');

  constructor(
    private commandService: CommandService
  ) {
  }

  getIndicatorTemplate(): TemplateRef<unknown> | null {
    return this.indicatorTemplate() ?? null;
  }

  editComment() {
    // Copy current comment to edit buffer
    this.editText.set(this.activity()?.viewModel?.content);
    this.isEditMode = true;
  }

  deleteComment() {
    this.commandService.execute(this.activity(), this.deleteCommandTemplate(), this);
  }

  reload(): void {
    return this.reloaded.emit();
  }

  updateComment() {
    let commandInput = new CommandInput();
    commandInput.name = this.editCommandTemplate().name;
    commandInput.addParameter("commandProviderId", this.activity().viewModel?.noteId);
    commandInput.addParameter("noteInput", {
        "content": this.editText()
    });
    this.commandService.sendCommand(commandInput, () => {
      this.reload();
      this.isEditMode = false;
    })
  }

  cancelUpdateComment() {
    this.editText.set(undefined);
    this.isEditMode = false;
  }

  hasContentChanged(): boolean {
    console.log('Checking content change');
    const text = this.editText();
    if (!text || !this.activity()?.viewModel?.content) return false;
    const currentJSON = text;
    const originalJSON = this.activity()?.viewModel?.content;
    return JSON.stringify(currentJSON) !== JSON.stringify(originalJSON);
  }

  onEmptyStateChange(isEmpty: boolean) {
    this.isEditorEmpty = isEmpty;
  }
}
